import { useLiveQuery } from 'dexie-react-hooks'
import { motion } from 'framer-motion'
import {
  BarChart3,
  CalendarRange,
  type LucideIcon,
  PieChart,
  TrendingUp,
  Users,
} from 'lucide-react'
import type { ReactNode } from 'react'

import { ScreenHeader } from '@/components/ScreenHeader'
import { Bars, HBars } from '@/components/charts/Bars'
import { Donut, type DonutSlice } from '@/components/charts/Donut'
import { Line } from '@/components/charts/Line'
import { Card } from '@/components/ui/card'
import { db } from '@/db/dexie'
import { topProduse, vanzariPeClient, vanzariPeZi } from '@/db/queries'
import { RO_LUNI_SCURT, fmtKg, fmtRON, fmtSuma, toDate } from '@/lib/format'

const round2 = (n: number) => Math.round(n * 100) / 100
const VANZARE = new Set(['Livrat', 'Facturat'])

const CATEGORIE_CULOARE: Record<string, string> = {
  Pui: 'var(--amber)',
  Organe: 'var(--tomato)',
  Procesate: 'var(--pine)',
}

/** ISO day → "24 iul." */
function ziScurt(zi: string): string {
  const d = toDate(zi)
  return `${d.getDate()} ${RO_LUNI_SCURT[d.getMonth()]}`
}

/** Local aggregate: kg livrate bucketed into 7-day weeks (newest window last). */
function useKgSaptamana() {
  const zile = useLiveQuery(() => vanzariPeZi(28), [])
  if (!zile) return undefined
  const weeks: { label: string; value: number }[] = []
  for (let end = zile.length; end > 0; end -= 7) {
    const slice = zile.slice(Math.max(0, end - 7), end)
    const kg = slice.reduce((s, d) => s + d.kg, 0)
    const last = slice[slice.length - 1]?.zi
    weeks.unshift({ label: last ? ziScurt(last) : '', value: round2(kg) })
  }
  return weeks
}

/** Local aggregate: RON per product category over delivered/invoiced orders. */
function useCategorii(): DonutSlice[] | undefined {
  return useLiveQuery(async () => {
    const [comenzi, produse] = await Promise.all([db.comenzi.toArray(), db.produse.toArray()])
    const catOf = new Map(produse.map((p) => [p.id, p.categorie]))
    const acc = new Map<string, number>()
    for (const c of comenzi) {
      if (!VANZARE.has(c.status)) continue
      for (const l of c.linii) {
        const k = catOf.get(l.produsId) ?? 'Altele'
        acc.set(k, (acc.get(k) ?? 0) + l.cantitateKg * l.pretKg)
      }
    }
    return [...acc.entries()]
      .map(([label, value]) => ({
        label,
        value: round2(value),
        color: CATEGORIE_CULOARE[label] ?? 'var(--taupe)',
      }))
      .sort((a, b) => b.value - a.value)
  }, [])
}

export default function Rapoarte() {
  const peZi = useLiveQuery(() => vanzariPeZi(14), [])
  const clienti = useLiveQuery(() => vanzariPeClient(8), [])
  const produseRaw = useLiveQuery(() => topProduse(14), [])
  const kgSapt = useKgSaptamana()
  const categorii = useCategorii()

  const peProdus = (produseRaw ?? [])
    .slice()
    .sort((a, b) => b.ron - a.ron)
    .slice(0, 8)

  return (
    <div>
      <ScreenHeader tab="rapoarte">
        <span className="hidden items-center gap-2 rounded-full border border-hairline bg-card px-3 py-1.5 text-sm text-muted-foreground shadow-paper sm:inline-flex">
          <CalendarRange className="size-4 text-amber" />
          Ultimele 14 zile
        </span>
      </ScreenHeader>

      <div className="grid gap-5 lg:grid-cols-2">
        <Reveal index={0} className="lg:col-span-2">
          <Panel
            title="Vânzări pe zi"
            hint="încasări din livrări, ultimele 14 zile"
            icon={TrendingUp}
          >
            <Line
              data={(peZi ?? []).map((d) => ({ label: d.label, value: d.ron }))}
              formatValue={fmtRON}
              labelStep={2}
              height={240}
            />
          </Panel>
        </Reveal>

        <Reveal index={1}>
          <Panel title="Vânzări pe produs" hint="valoare livrată, RON" icon={BarChart3}>
            {peProdus.length > 0 ? (
              <HBars
                data={peProdus.map((p) => ({ label: p.nume, value: p.ron }))}
                formatValue={fmtSuma}
              />
            ) : (
              <EmptyLine />
            )}
          </Panel>
        </Reveal>

        <Reveal index={2}>
          <Panel title="Top clienți" hint="cifră de afaceri, RON" icon={Users}>
            {clienti && clienti.length > 0 ? (
              <HBars
                data={clienti.map((c) => ({ label: c.nume, value: c.ron }))}
                formatValue={fmtSuma}
              />
            ) : (
              <EmptyLine />
            )}
          </Panel>
        </Reveal>

        <Reveal index={3}>
          <Panel title="Kg pe săptămână" hint="kg livrate / săptămână" icon={CalendarRange}>
            <Bars data={kgSapt ?? []} formatValue={fmtKg} height={240} />
          </Panel>
        </Reveal>

        <Reveal index={4}>
          <Panel
            title="Vânzări pe categorie"
            hint="repartiție pe categorii de produs"
            icon={PieChart}
          >
            <div className="py-2">
              <Donut data={categorii ?? []} formatValue={fmtRON} centerLabel="total" />
            </div>
          </Panel>
        </Reveal>
      </div>
    </div>
  )
}

// ── local building blocks ────────────────────────────────────────────────────

function Panel({
  title,
  hint,
  icon: Icon,
  children,
}: {
  title: string
  hint?: string
  icon?: LucideIcon
  children: ReactNode
}) {
  return (
    <Card className="h-full gap-0 p-5">
      <div className="mb-4 flex items-center gap-2.5">
        {Icon && (
          <span className="flex size-7 shrink-0 items-center justify-center rounded-lg bg-amber-soft text-amber-ink">
            <Icon className="size-4" />
          </span>
        )}
        <div className="min-w-0">
          <h2 className="font-display text-base font-medium leading-tight">{title}</h2>
          {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
        </div>
      </div>
      {children}
    </Card>
  )
}

function Reveal({
  index,
  className,
  children,
}: {
  index: number
  className?: string
  children: ReactNode
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.07, ease: [0.16, 1, 0.3, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  )
}

function EmptyLine() {
  return <p className="py-6 text-center text-sm text-muted-foreground">Nu există date încă.</p>
}
